"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IndustryRoutingGrid() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const pathways = [{
    number: "01",
    kanji: "標",
    title: "Industrial Labeling",
    description: "Precision-engineered identification solutions for complex environments, ensuring compliance and clarity across all your facility infrastructure.",
    details: ["Durable phenolic materials", "High-contrast engraving", "Custom mounting options"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_engraving.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_engraving.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_labeling_engraving.webp-wvc-sizes",
    alt: "Precision industrial labeling equipment in operation",
    to: "/ArcFlashLabelsPage"
  }, {
    number: "02",
    kanji: "刻",
    title: "Direct Part Marking",
    description: "Advanced DPM services providing permanent, traceable identification directly onto metal components for aerospace, medical, and automotive sectors.",
    details: ["Laser-etched serialization", "High-precision tolerances", "Material-specific calibration"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_metal_marking.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_metal_marking.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_metal_marking.png-wvc-sizes",
    alt: "Laser marking process on a metal component",
    to: "/DirectPartMarkingDPMPage"
  }, {
    number: "03",
    kanji: "製",
    title: "Custom Fabrication",
    description: "Bespoke manufacturing and submittal-ready documentation for specialized signage, control panels, and architectural identification hardware.",
    details: ["Technical submittal support", "Custom material sourcing", "Rapid prototyping cycles"],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_fabrication_metal.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_fabrication_metal.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_fabrication_metal.png-wvc-sizes",
    alt: "Finished custom metal fabrication project",
    to: "/SubmittalPackagesPage"
  }];
  return;
}
function PathwayCard({
  pathway,
  index
}: {
  pathway: {
    number: string;
    kanji: string;
    title: string;
    description: string;
    details: string[];
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    to: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} data-index={index} className={`group relative flex flex-col border border-border bg-card transition-all duration-700 ease-out hover:border-[var(--chart-3)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <div className="flex flex-col h-full p-8 md:p-10">
        {}
        <div className="flex items-baseline justify-between mb-8">
          <span className="font-serif font-default text-6xl md:text-7xl text-foreground leading-none">
            {pathway.number}
          </span>
          <span className="font-serif text-4xl md:text-5xl leading-none" style={{
          color: "var(--chart-3)"
        }}>
            {pathway.kanji}
          </span>
        </div>

        {}
        <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-4 tracking-tight">
          {pathway.title}
        </h3>

        {}
        <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-8">
          {pathway.description}
        </p>

        {}
        <ul className="space-y-2 mb-10">
          {pathway.details.map((detail, di) => <li data-index={di} key={di} className="font-mono text-xs uppercase tracking-wider text-foreground/80 flex items-start gap-3">
              <span className="text-muted-foreground" aria-hidden="true">—</span>
              <span>{detail}</span>
            </li>)}
        </ul>

        {}
        <div className="relative overflow-hidden mb-8 mt-auto">
          <img src={pathway.image} data-wvc-srcset={pathway.srcset} data-wvc-sizes={pathway.sizes} alt={pathway.alt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        </div>

        {}
        <Button asChild variant="ghost" className="w-full justify-between px-0 hover:bg-transparent text-foreground hover:text-[var(--chart-3)] border-t border-border rounded-md pt-6">
          <Link to={pathway.to}>
            <span className="font-mono text-sm uppercase tracking-[0.2em]">View Details</span>
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Button>
      </div>
    </div>;
}